import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  CreditCard as CreditCardIcon,
  AccountBalance as BankIcon,
  Store as StoreIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { isWireTransferEnabled } from '../utils/featureFlags';

const PaymentMethodSelector = ({
  selectedMethod,
  onMethodSelect,
  onCardDetailsChange,
  cardDetails,
  amount,
  loading = false,
  error = null
}) => {
  const { t } = useTranslation(['planSelector', 'common']);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(true);
  const [cardValidation, setCardValidation] = useState({});

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      setMethodsLoading(true);
      // In a real implementation, this would fetch available payment methods
      // For now, we'll use static data
      const methods = [
        {
          method: 'credit_card',
          name: 'Credit Card',
          description: 'Pay instantly with your credit or debit card',
          icon: CreditCardIcon,
          processingTime: 'Instant',
          available: true
        },
        {
          method: 'wire_transfer',
          name: 'Wire Transfer',
          description: 'Bank transfer with reference code',
          icon: BankIcon,
          processingTime: '1-2 business days',
          available: true
        },
        {
          method: 'in_place',
          name: 'Pay at Gym',
          description: 'Pay in cash or card at the gym reception',
          icon: StoreIcon,
          processingTime: 'Visit required',
          available: true
        }
      ];

      // Filter wire transfer if feature flag is disabled
      const filteredMethods = methods.filter(method =>
        method.method !== 'wire_transfer' || isWireTransferEnabled()
      );

      setPaymentMethods(filteredMethods);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
    } finally {
      setMethodsLoading(false);
    }
  };

  const validateCardNumber = (number) => {
    const cleaned = number.replace(/\s/g, '');
    const isValid = /^\d{13,19}$/.test(cleaned);
    return {
      isValid,
      message: isValid ? '' : 'Invalid card number'
    };
  };

  const validateExpiryDate = (expiry) => {
    const isValid = /^(0[1-9]|1[0-2])\/\d{2}$/.test(expiry);
    if (!isValid) return { isValid: false, message: 'Format: MM/YY' };

    const [month, year] = expiry.split('/');
    const currentDate = new Date();
    const expiryDate = new Date(2000 + parseInt(year), parseInt(month) - 1);

    if (expiryDate <= currentDate) {
      return { isValid: false, message: 'Card has expired' };
    }

    return { isValid: true, message: '' };
  };

  const validateCVV = (cvv) => {
    const isValid = /^\d{3,4}$/.test(cvv);
    return {
      isValid,
      message: isValid ? '' : 'Invalid CVV'
    };
  };

  const handleCardDetailChange = (field, value) => {
    let formattedValue = value;

    // Format card number with spaces
    if (field === 'cardNumber') {
      formattedValue = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      if (formattedValue.length > 19) return;
    }

    // Format expiry date
    if (field === 'expiryDate') {
      formattedValue = value.replace(/\D/g, '');
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.substring(0, 2) + '/' + formattedValue.substring(2, 4);
      }
      if (formattedValue.length > 5) return;
    }

    // Limit CVV length
    if (field === 'cvv' && value.length > 4) return;

    const newCardDetails = { ...cardDetails, [field]: formattedValue };

    // Validate fields
    const validation = { ...cardValidation };
    if (field === 'cardNumber') {
      validation.cardNumber = validateCardNumber(formattedValue);
    } else if (field === 'expiryDate') {
      validation.expiryDate = validateExpiryDate(formattedValue);
    } else if (field === 'cvv') {
      validation.cvv = validateCVV(formattedValue);
    }

    setCardValidation(validation);
    onCardDetailsChange(newCardDetails);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  if (methodsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('planSelector.paymentMethod.title')}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('planSelector.paymentMethod.subtitle', { amount: formatAmount(amount) })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <FormControl component="fieldset" fullWidth>
        <RadioGroup
          value={selectedMethod}
          onChange={(e) => onMethodSelect(e.target.value)}
        >
          {paymentMethods.map((method) => {
            const IconComponent = method.icon;
            return (
              <Card
                key={method.method}
                variant="outlined"
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  border: selectedMethod === method.method ? 2 : 1,
                  borderColor: selectedMethod === method.method ? 'primary.main' : 'divider',
                  '&:hover': {
                    borderColor: 'primary.main'
                  }
                }}
                onClick={() => onMethodSelect(method.method)}
              >
                <CardContent>
                  <FormControlLabel
                    value={method.method}
                    control={<Radio />}
                    label={
                      <Box display="flex" alignItems="center" width="100%">
                        <IconComponent sx={{ mr: 2, color: 'primary.main' }} />
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight="medium">
                            {method.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {method.description}
                          </Typography>
                          <Chip
                            label={method.processingTime}
                            size="small"
                            variant="outlined"
                            sx={{ mt: 1 }}
                          />
                        </Box>
                      </Box>
                    }
                    sx={{ width: '100%', margin: 0 }}
                  />
                </CardContent>
              </Card>
            );
          })}
        </RadioGroup>
      </FormControl>

      {/* Credit Card Details Form */}
      {selectedMethod === 'credit_card' && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {t('planSelector.paymentMethod.creditCard.details')}
            </Typography>

            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label={t('planSelector.paymentMethod.creditCard.cardNumber')}
                  value={cardDetails.cardNumber || ''}
                  onChange={(e) => handleCardDetailChange('cardNumber', e.target.value)}
                  placeholder="1234 5678 9012 3456"
                  error={cardValidation.cardNumber?.isValid === false}
                  helperText={cardValidation.cardNumber?.message}
                  inputProps={{ maxLength: 19 }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('planSelector.paymentMethod.creditCard.cardHolder')}
                  value={cardDetails.cardHolder || ''}
                  onChange={(e) => handleCardDetailChange('cardHolder', e.target.value)}
                  placeholder="John Doe"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label={t('planSelector.paymentMethod.creditCard.expiryDate')}
                  value={cardDetails.expiryDate || ''}
                  onChange={(e) => handleCardDetailChange('expiryDate', e.target.value)}
                  placeholder="MM/YY"
                  error={cardValidation.expiryDate?.isValid === false}
                  helperText={cardValidation.expiryDate?.message}
                  inputProps={{ maxLength: 5 }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  label={t('planSelector.paymentMethod.creditCard.cvv')}
                  value={cardDetails.cvv || ''}
                  onChange={(e) => handleCardDetailChange('cvv', e.target.value)}
                  placeholder="123"
                  type="password"
                  error={cardValidation.cvv?.isValid === false}
                  helperText={cardValidation.cvv?.message}
                  inputProps={{ maxLength: 4 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}


      {/* In-Place Payment Instructions */}
      {selectedMethod === 'in_place' && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <InfoIcon color="info" sx={{ mr: 1 }} />
              <Typography variant="subtitle1">
                {t('planSelector.paymentMethod.inPlace.instructions')}
              </Typography>
            </Box>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('planSelector.paymentMethod.inPlace.step1')} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('planSelector.paymentMethod.inPlace.step2')} />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t('planSelector.paymentMethod.inPlace.step3')} />
              </ListItem>
            </List>
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('planSelector.paymentMethod.inPlace.note')}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default PaymentMethodSelector;